/**
 * TierGate - Component for tier-based feature gating
 * Shows upgrade prompts for features not available in current tier
 * 
 * Usage:
 * - Basic tier → read-only + subtle "Upgrade to enable" affordance
 * - Pro/Enterprise → full access
 */

import React from 'react';
import { Lock, Sparkles, ArrowRight, Crown, Star } from 'lucide-react';
import { useProductMode } from '../context/ProductModeContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';

// Tier configuration
const TIER_CONFIG = {
  basic: {
    level: 1,
    name: 'Basic',
    color: 'gray',
    bgGradient: 'from-gray-100 to-gray-50',
    borderColor: 'border-gray-200',
    textColor: 'text-gray-600',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-700',
  },
  pro: {
    level: 2,
    name: 'Pro',
    color: 'blue',
    bgGradient: 'from-blue-100 to-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
  },
  enterprise: {
    level: 3,
    name: 'Enterprise',
    color: 'purple',
    bgGradient: 'from-purple-100 to-indigo-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-600',
    badgeBg: 'bg-gradient-to-r from-purple-100 to-indigo-100',
    badgeText: 'text-purple-700',
  }
};

/**
 * TierGate component wraps content and shows upgrade prompt if feature is gated
 * 
 * @param {string} requiredTier - Minimum tier required ('basic', 'pro', 'enterprise')
 * @param {string} feature - Feature name for display
 * @param {string} description - Description of the gated feature
 * @param {React.ReactNode} children - Content to show if tier is sufficient
 * @param {string} variant - 'inline' | 'card' | 'overlay' | 'badge' | 'disabled'
 * @param {function} onUpgrade - Optional callback when upgrade is clicked
 * @param {boolean} showReadOnly - Show content in read-only mode instead of hiding
 */
const TierGate = ({ 
  requiredTier = 'pro',
  feature,
  description,
  children,
  variant = 'card',
  onUpgrade,
  showReadOnly = false,
  className = ''
}) => {
  const { scopingTier, isBasicTier, isProTier, isEnterpriseTier, isAdminBypass } = useProductMode();
  
  // Get tier configs
  const currentTierConfig = TIER_CONFIG[scopingTier] || TIER_CONFIG.basic;
  const requiredTierConfig = TIER_CONFIG[requiredTier] || TIER_CONFIG.pro;
  
  // Determine if current tier is sufficient
  // RBAC FIX: Admins bypass all tier restrictions
  const hasAccess = isAdminBypass || currentTierConfig.level >= requiredTierConfig.level;
  
  // Track that upgrade prompt was shown (must be before any returns per React hooks rules)
  React.useEffect(() => {
    if (!hasAccess && !isAdminBypass) {
      trackEvent(TELEMETRY_EVENTS.TIER_UPGRADE_PROMPT_SHOWN, {
        currentTier: scopingTier,
        requiredTier,
        feature
      });
    }
  }, [hasAccess, isAdminBypass, scopingTier, requiredTier, feature]);
  
  // Track upgrade prompt shown
  const handleUpgradeClick = () => {
    trackEvent(TELEMETRY_EVENTS.TIER_UPGRADE_CLICKED, {
      currentTier: scopingTier,
      requiredTier,
      feature
    });
    
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Default: navigate to billing
      window.location.href = '/admin/billing?upgrade=true';
    }
  };
  
  // If user has access (including admin bypass), render children
  if (hasAccess) {
    return <>{children}</>;
  }
  
  // Inline variant - minimal upgrade hint (for buttons, links)
  if (variant === 'inline') {
    return (
      <span 
        className={`inline-flex items-center gap-1 text-xs text-gray-400 cursor-not-allowed ${className}`}
        title={`Requires ${requiredTierConfig.name} tier`}
        data-testid="tier-gate-inline"
      >
        <Lock className="w-3 h-3" />
        <span>{requiredTierConfig.name}</span>
      </span>
    );
  }
  
  // Badge variant - small badge next to content
  if (variant === 'badge') {
    return (
      <div className={`relative ${className}`}>
        {showReadOnly ? (
          <div className="opacity-60 pointer-events-none">
            {children}
          </div>
        ) : null}
        <span 
          className="absolute -top-1 -right-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded-full shadow-sm cursor-pointer hover:bg-amber-200 transition-colors"
          onClick={handleUpgradeClick}
          data-testid="tier-gate-badge"
        >
          <Crown className="w-2.5 h-2.5" />
          {requiredTierConfig.name}
        </span>
      </div>
    );
  }
  
  // Disabled variant - shows content but disabled with tooltip
  if (variant === 'disabled') {
    return (
      <div 
        className={`relative group ${className}`}
        data-testid="tier-gate-disabled"
      >
        <div className="opacity-50 pointer-events-none select-none">
          {children}
        </div>
        {/* Tooltip on hover */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
          <div className="flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            Upgrade to {requiredTierConfig.name} to enable
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      </div>
    );
  }
  
  // Overlay variant - shows content with overlay
  if (variant === 'overlay') {
    return (
      <div className={`relative ${className}`} data-testid="tier-gate-overlay">
        <div className="opacity-30 pointer-events-none blur-[1px] select-none">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50/90 to-white/95 backdrop-blur-sm rounded-lg">
          <div className="text-center p-4 max-w-xs">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">
              Upgrade to {requiredTierConfig.name}
            </h4>
            <p className="text-sm text-gray-500 mb-3">
              {description || `This feature requires the ${requiredTierConfig.name} tier or higher.`}
            </p>
            <button
              onClick={handleUpgradeClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              <Sparkles className="w-4 h-4" />
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Card variant (default) - full upgrade card
  return (
    <div 
      className={`bg-gradient-to-br ${requiredTierConfig.bgGradient} rounded-xl border ${requiredTierConfig.borderColor} p-6 text-center ${className}`}
      data-testid="tier-gate-card"
    >
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
        <Lock className="w-7 h-7 text-purple-600" />
      </div>
      
      <h3 className="font-semibold text-lg text-gray-900 mb-2">
        {feature || 'Premium Feature'}
      </h3>
      
      <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
        {description || `This feature is available in the ${requiredTierConfig.name} tier and above. Upgrade to unlock ${feature || 'this capability'}.`}
      </p>
      
      <div className="flex items-center justify-center gap-3 mb-4">
        <TierBadge tier={scopingTier} active />
        <ArrowRight className="w-4 h-4 text-gray-300" />
        <TierBadge tier={requiredTier} highlighted />
      </div>
      
      <button
        onClick={handleUpgradeClick}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg shadow-purple-500/25 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
      >
        <Sparkles className="w-4 h-4" />
        Upgrade to {requiredTierConfig.name}
      </button>
      
      <p className="mt-3 text-xs text-gray-400">
        Contact sales for enterprise features
      </p>
    </div>
  );
};

/**
 * TierBadge - Visual badge for tier display
 */
const TierBadge = ({ tier, active = false, highlighted = false }) => {
  const colors = {
    basic: 'bg-gray-100 text-gray-600 border-gray-200',
    pro: 'bg-blue-100 text-blue-700 border-blue-200',
    enterprise: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  
  const highlightColors = {
    basic: 'bg-gray-500 text-white border-gray-600',
    pro: 'bg-blue-600 text-white border-blue-700',
    enterprise: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-700',
  };
  
  return (
    <span 
      className={`px-3 py-1 text-xs font-semibold rounded-full border ${
        highlighted ? highlightColors[tier] : colors[tier]
      } ${active ? 'ring-2 ring-offset-1 ring-gray-300' : ''}`}
    >
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </span>
  );
};

/**
 * Hook for checking tier capabilities
 * RBAC FIX: Admins bypass tier restrictions
 */
export const useTierGate = (requiredTier = 'pro') => {
  const { scopingTier, isAdminBypass } = useProductMode();
  
  // RBAC FIX: Admins always have access
  if (isAdminBypass) {
    return {
      hasAccess: true,
      currentTier: 'enterprise', // Admins treated as enterprise
      requiredTier,
      isUpgradeRequired: false
    };
  }
  
  const tierOrder = { basic: 1, pro: 2, enterprise: 3 };
  const currentTierLevel = tierOrder[scopingTier] || 1;
  const requiredTierLevel = tierOrder[requiredTier] || 2;
  const hasAccess = currentTierLevel >= requiredTierLevel;
  
  return {
    hasAccess,
    currentTier: scopingTier,
    requiredTier,
    isUpgradeRequired: !hasAccess
  };
};

export { TierBadge };
export default TierGate;
